<template>
  <v-card style="margin-bottom:5em">
    <v-toolbar>
      <v-toolbar-title>Academic Program/Advisor Change Admin</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on" @click="loadData()">
            <v-icon>fal fa-sync</v-icon>
          </v-btn>
        </template>
        <span>Refresh the data</span>
      </v-tooltip>
      <v-text-field ref="searchField" v-model="searchText" label="Search (Name/Banner ID)" style="max-width:260px" prepend-inner-icon="fal fa-search" clear-icon="fal fa-times" hide-details clearable outlined dense class="mr-2" @blur="searchBlur" />
      <v-select v-model="term" :items="terms" label="Term" outlined hide-details dense style="max-width:200px" />
    </v-toolbar>
    <v-card-text>
      <v-row>
        <v-col v-for="{ value, text, count } of filterOptions" :key="value">
          <v-badge :content="count">
            <v-btn :disabled="count === 0" :color="filter === value ? 'primary' : ''" @click="filter = (filter === value ? null : value)">{{ text }}</v-btn>
          </v-badge>
        </v-col>
      </v-row>
    </v-card-text>
    <v-data-table :items="students" :headers="headers" :server-items-length="totalCount" :options="options" :footer-props="{ 'items-per-page-options': [5, 10, 15, 20, 25] }" @update:options="updateOptions" @dblclick:row="(x, { item: { _id } }) => $router.push('/student/program-change/admin/' + _id)">
      <template v-slot:item.createdAt="{ item }">{{ stringFormatDate(item.createdAt) }}</template>
      <template v-slot:item.action="{ item }">
        <v-btn icon :to="'/student/program-change/admin/' + item._id">
          <v-icon>fal fa-eye</v-icon>
        </v-btn>
      </template>
      <template v-slot:item.status="{ item }">
        <span v-if="isRecords">{{ filterOptions.reduce((prev, { value, text}) => value === item.status ? text : prev, item.status) }}</span>
        <span v-else>{{ item.advisorApproval.filter(({ pidm }) => pidm === user.pidm)[0].approved ? 'Approved' : 'Pending' }}</span>
      </template>
    </v-data-table>
  </v-card>
</template>
<script>
import { ref, computed, onMounted, watch } from '@vue/composition-api'
import { stringFormatDate } from '../../../../helpers/formatters'
import { inArray } from '../../../../helpers/functions'

export default {
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const roles = computed(() => root.$store.state.roles)
    const isRecords = computed(() => user.value.roles.find((role) => ['Records', 'Technology Services'].includes(role)))
    const term = computed({
      get: () => root.$store.state.student.programAdmin.term,
      set: (term) => root.$store.commit('student/setProgramAdminFields', { term })
    })
    const terms = ref([])
    const students = ref([])
    const totalCount = ref(0)
    const headers = ref([
      { text: 'Term', value: 'term' },
      { text: 'Banner ID', value: 'bannerId' },
      { text: 'Name', value: 'name' },
      { text: 'Submitted Date', value: 'createdAt' },
      { text: 'Status', value: 'status', sort: false },
      { text: 'View', value: 'action', sort: false }
    ])

    async function getCounts () {
      if (isRecords.value) {
        const aggregate = [
          { $match: { term: term.value } },
          { $group: { _id: '$status', count: { $sum: 1 } } }
        ]
        const { data } = await root.$feathers.service('student/major-change').find({ query: { aggregate } })
        console.log(data)
        const hash = { Submitted: 0, Pending: 0, Approved: 0, Processed: 0, Returned: 0 }
        data.forEach(({ _id, count }) => { hash[_id] = count })
        filterOptions.value = [
          { value: 'Submitted', text: 'Pending Advisor Review by Records', count: hash.Submitted },
          { value: 'Pending', text: 'Pending Advisor Approval', count: hash.Pending },
          { value: 'Approved', text: 'Approved, Ready to Process', count: hash.Approved },
          { value: 'Processed', text: 'Processed in Banner', count: hash.Processed },
          { value: 'Returned', text: 'Returned to Student', count: hash.Returned }
        ]
      } else {
        const aggregate = [
          { $match: { term: term.value, status: 'Pending' } },
          { $unwind: '$advisorApproval' },
          { $match: { 'advisorApproval.pidm': user.value.pidm } },
          { $group: { _id: '$advisorApproval.approved', count: { $sum: 1 } } }
        ]
        const { data } = await root.$feathers.service('student/major-change').find({ query: { aggregate } })
        const hash = { Pending: 0, Approved: 0 }
        data.forEach(({ _id, count }) => { hash[_id ? 'Approved' : 'Pending'] += count })
        filterOptions.value = [
          { value: false, text: 'Pending Approval', count: hash.Pending },
          { value: true, text: 'Approved', count: hash.Approved }
        ]
      }
    }

    const searchActive = ref(false)
    const searchField = ref(null)
    const searchText = computed({
      get: () => root.$store.state.student.programAdmin.searchText || '',
      set: (val) => root.$store.commit('student/setProgramAdminFields', { searchText: val || '' })
    })

    function activateSearch () {
      searchActive.value = true
      root.$nextTick(() => searchField.value.focus())
    }

    function searchBlur () {
      if (searchText.value === '' || searchText.value == null) {
        searchActive.value = false
      }
    }

    const filter = computed({
      get: () => root.$store.state.student.programAdmin.filter,
      set: (val) => root.$store.commit('student/setProgramAdminFields', { filter: val })
    })
    const filterOptions = ref([])

    const options = computed(() => root.$store.state.student.programAdmin.options || { page: 1, itemsPerPage: 10, sortBy: ['createdAt'], sortDesc: [1] })
    function updateOptions (opt) {
      root.$store.commit('student/setProgramAdminFields', { options: opt })
      loadData()
    }
    watch([searchText, filter], () => {
      loadData()
    })
    function loadData () {
      const query = {
        term: term.value,
        $sort: {}
      }
      if (!isRecords.value) {
        const $elemMatch = { pidm: user.value.pidm }
        if (filter.value != null) {
          $elemMatch.approved = filter.value
        }
        query.status = 'Pending'
        query.advisorApproval = { $elemMatch }
      } else if (filter.value) {
        query.status = filter.value
      }
      if (searchText.value !== '') {
        if (searchText.value.substring(0, 1) === '@') query.bannerId = { $regex: searchText.value }
        else query.name = { $regex: searchText.value, $options: 'i' }
      }
      const { page, itemsPerPage, sortBy, sortDesc } = options.value
      query.$limit = itemsPerPage
      query.$skip = (page - 1) * itemsPerPage
      for (let i = 0; i < sortBy.length; i++) {
        query.$sort[sortBy[i]] = sortDesc[i] ? 1 : -1
      }
      root.$feathers.service('student/major-change').find({ query }).then(({ data, total }) => {
        totalCount.value = total
        students.value = data
      })
    }

    watch(() => term, () => {
      if (term.value !== '' && term.value != null) {
        getCounts()
        loadData()
      }
    })

    onMounted(() => {
      if (!isRecords.value && filter.value.length === 0) {
        filter.value.push(false)
      }
      root.$feathers.service('student/major-change').find({ query: { $distinct: 'term' } }).then(({ data }) => {
        terms.value = data.map(({ _id }) => _id).sort().reverse()
        term.value = terms.value[0]
        getCounts()
        loadData()
      })
    })

    return {
      user,
      roles,
      isRecords,
      term,
      terms,
      students,
      totalCount,
      headers,
      searchActive,
      searchField,
      searchText,
      activateSearch,
      searchBlur,
      filter,
      filterOptions,
      options,
      updateOptions,
      loadData,
      stringFormatDate,
      inArray
    }
  }
}
</script>
